import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { History, User, Clock } from "lucide-react";
import { OrderChangeLog, Order, ORDER_STATUS_LABELS, OrderStatus } from "@/types/tms";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ChangeEventReason,
  ChangeEventType,
  EVENT_LABELS,
  REASON_LABELS,
} from "@/lib/change-events";

interface OrderHistoryProps {
  order: Order;
}

interface ChangeEventRow {
  id: string;
  event_type: ChangeEventType;
  reason: ChangeEventReason;
  other_text: string | null;
  created_by_email: string | null;
  created_at: string;
}

const FIELD_LABELS: Record<string, string> = {
  status: "Status",
  ship_from_city: "Ship From City",
  ship_to_city: "Ship To City",
  ready_time: "Ready Time",
  ready_time_end: "Ready Time (End)",
  requested_delivery_time: "Delivery Time",
  requested_delivery_time_end: "Delivery Time (End)",
  trailer_id: "Trailer ID",
  trailer_type: "Trailer Type",
  trailer_type_id: "Trailer Type",
  tender_id: "Tender ID",
  assigned_tractor_id: "Assigned Tractor",
  assigned_operator_id: "Assigned Operator",
  assigned_operator_2_id: "Secondary Operator",
  trip_type: "Trip Type",
  actual_departure_time: "Actual Departure",
  actual_arrival_time: "Actual Arrival",
  bill_of_lading: "Bill of Lading",
  leg_1_driver_status: "Leg 1 Status",
  leg_2_driver_status: "Leg 2 Status",
};

/**
 * Fallback humanizer for any field not in FIELD_LABELS.
 * Strips a trailing `_id`, splits on `_`, and Title Cases each word.
 */
function humanizeFieldName(field: string): string {
  return field
    .replace(/_id$/, "")
    .split("_")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/**
 * Humanize an enum-like value (e.g. `in_transit` → `In Transit`). Used as a
 * fallback when no explicit translation exists.
 */
function humanizeEnumValue(value: string): string {
  return value
    .split("_")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/** Fields whose values are status-like enums and should use ORDER_STATUS_LABELS. */
const STATUS_VALUE_FIELDS = new Set<string>([
  "status",
  "leg_1_driver_status",
  "leg_2_driver_status",
  "leg_3_driver_status",
  "leg_4_driver_status",
]);

export function OrderHistory({ order }: OrderHistoryProps) {
  const { data: changeLog, isLoading } = useQuery({
    queryKey: ["order-change-log", order.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("order_change_log")
        .select("*")
        .eq("order_id", order.id)
        .order("changed_at", { ascending: false });
      if (error) throw error;
      return data as OrderChangeLog[];
    },
  });

  const { data: changeEvents, isLoading: isLoadingEvents } = useQuery({
    queryKey: ["order-change-events", order.id],
    queryFn: async () => {
      const { data, error } = await (supabase
        .from("change_events" as any)
        .select("id, event_type, reason, other_text, created_by_email, created_at")
        .eq("order_id", order.id)
        .order("created_at", { ascending: false }) as any);
      if (error) throw error;
      return (data ?? []) as ChangeEventRow[];
    },
  });

  const formatValue = (fieldName: string, value: string | null) => {
    if (!value || value === "null") return "—";
    if (fieldName.includes("time")) {
      try {
        return format(new Date(value), "MMM dd 'at' hh:mm a");
      } catch {
        return value;
      }
    }
    if (STATUS_VALUE_FIELDS.has(fieldName)) {
      return (
        ORDER_STATUS_LABELS[value as OrderStatus] ?? humanizeEnumValue(value)
      );
    }
    return value;
  };

  return (
    <div className="border rounded-lg p-4 bg-muted/30">
      <h4 className="font-medium mb-4 flex items-center gap-2">
        <History className="h-4 w-4" /> Order History
      </h4>

      {/* Creation Info */}
      <div className="mb-4 pb-4 border-b">
        <div className="flex items-center gap-2 text-sm">
          <User className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-muted-foreground">Created by:</span>
          <span className="font-medium">{order.created_by || "Unknown"}</span>
        </div>
        <div className="flex items-center gap-2 text-sm mt-1">
          <Clock className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-muted-foreground">Created at:</span>
          <span className="font-medium">
            {format(new Date(order.created_at), "MMM dd 'at' hh:mm a")}
          </span>
        </div>
      </div>

      {/* Change Log */}
      <div className="space-y-3">
        <h5 className="text-sm font-medium text-muted-foreground">Change Log</h5>
        {isLoading || isLoadingEvents ? (
          <div className="space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : (changeLog && changeLog.length > 0) || (changeEvents && changeEvents.length > 0) ? (
          <div className="space-y-2 max-h-[200px] overflow-y-auto">
            {[
              ...(changeEvents ?? []).map((evt) => ({
                kind: "event" as const,
                at: evt.created_at,
                data: evt,
              })),
              ...(changeLog ?? []).map((entry) => ({
                kind: "log" as const,
                at: entry.changed_at,
                data: entry,
              })),
            ]
              .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())
              .map((item) =>
                item.kind === "event" ? (
                  <div
                    key={`evt-${item.data.id}`}
                    className="text-sm p-2 rounded bg-background border border-primary/40"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium">
                        {EVENT_LABELS[item.data.event_type] ?? item.data.event_type}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {format(new Date(item.data.created_at), "MMM dd 'at' hh:mm a")}
                      </span>
                    </div>
                    <div className="text-muted-foreground">
                      Reason:{" "}
                      <span className="text-foreground">
                        {REASON_LABELS[item.data.reason] ?? item.data.reason}
                      </span>
                      {item.data.other_text ? (
                        <span className="ml-2 italic">"{item.data.other_text}"</span>
                      ) : null}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      by {item.data.created_by_email ?? "system"}
                    </div>
                  </div>
                ) : (
                  <div
                    key={`log-${item.data.id}`}
                    className="text-sm p-2 rounded bg-background border"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium">
                        {FIELD_LABELS[item.data.field_name] || humanizeFieldName(item.data.field_name)}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {format(new Date(item.data.changed_at), "MMM dd 'at' hh:mm a")}
                      </span>
                    </div>
                    <div className="text-muted-foreground">
                      <span className="line-through">
                        {formatValue(item.data.field_name, item.data.old_value)}
                      </span>
                      <span className="mx-2">→</span>
                      <span className="text-foreground">
                        {formatValue(item.data.field_name, item.data.new_value)}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      by {item.data.changed_by}
                    </div>
                  </div>
                )
              )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No changes recorded yet.</p>
        )}
      </div>
    </div>
  );
}
